import { useRef, type KeyboardEvent } from 'react';

import PricingStep, { ResultEmailPromptModal } from '../PricingStep';
import PaidScanPaymentModal, { type PaidScanPaymentSubmission } from '../PaidScanPaymentModal';
import { isStripeConfigured } from '../../stripeConfig';
import type {
  LaunchState,
  LoadState,
  PricingEstimateResponse,
  PricingPlan,
  ScanAcceptedResponse,
  ScanAllowanceResponse
} from '../workflowShared';

const formatNextScanAt = (nextScanAt: number | undefined): string => {
  return nextScanAt ? new Date(nextScanAt).toLocaleString() : 'tomorrow';
};

const PRICING_PLANS: { id: PricingPlan; label: string }[] = [
  { id: 'full', label: 'Full pentest' },
  { id: 'light', label: 'Light pentest' },
  { id: 'free', label: 'Free' }
];

const formatPlanPrice = (totalUsd: number | undefined): string => {
  if (totalUsd === undefined) {
    return '';
  }

  return totalUsd === 0
    ? 'Free'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(totalUsd);
};

export function RunPricingScreen({
  canRunScan,
  selectedRepositoryLabel,
  workspaceName,
  pricingEstimateState,
  pricingEstimateError,
  pricingEstimate,
  launchState,
  launchError,
  scanResponse,
  freeScanAllowance,
  isEmailPromptOpen,
  resultEmail,
  selectedPlan,
  isPaymentOpen,
  onSelectPlan,
  onOpenPayment,
  onClosePayment,
  onPaymentError,
  onSubmitPayment,
  onOpenEmailPrompt,
  onCloseEmailPrompt,
  onSubmitEmail,
  onViewRun,
  onGoToScope
}: {
  canRunScan: boolean;
  selectedRepositoryLabel: string;
  workspaceName?: string;
  pricingEstimateState: LoadState;
  pricingEstimateError: string;
  pricingEstimate: PricingEstimateResponse | null;
  launchState: LaunchState;
  launchError: string;
  scanResponse: ScanAcceptedResponse | null;
  freeScanAllowance: ScanAllowanceResponse | null;
  isEmailPromptOpen: boolean;
  resultEmail: string;
  selectedPlan: PricingPlan;
  isPaymentOpen: boolean;
  onSelectPlan: (plan: PricingPlan) => void;
  onOpenPayment: () => void;
  onClosePayment: () => void;
  onPaymentError: (message: string) => void;
  onSubmitPayment: (submission: PaidScanPaymentSubmission) => Promise<void>;
  onOpenEmailPrompt: () => void;
  onCloseEmailPrompt: () => void;
  onSubmitEmail: (email: string) => void;
  onViewRun: (runId: string) => void;
  onGoToScope: () => void;
}) {
  const hasAcceptedScan = launchState === 'accepted';
  const isFreeScanUsed = freeScanAllowance?.canScan === false;
  const selectedPlanInfo = PRICING_PLANS.find((plan) => plan.id === selectedPlan) ?? PRICING_PLANS[0];
  const selectedPlanEstimate = pricingEstimate?.plans?.[selectedPlan];
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // WAI-ARIA tabs pattern: arrow keys move between plans, Home/End jump to the ends.
  const selectPlanFromKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = PRICING_PLANS.findIndex((plan) => plan.id === selectedPlan);
    const lastIndex = PRICING_PLANS.length - 1;
    const nextIndex = {
      ArrowRight: currentIndex === lastIndex ? 0 : currentIndex + 1,
      ArrowLeft: currentIndex === 0 ? lastIndex : currentIndex - 1,
      Home: 0,
      End: lastIndex
    }[event.key];

    if (nextIndex === undefined) {
      return;
    }

    event.preventDefault();
    onSelectPlan(PRICING_PLANS[nextIndex].id);
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <main className="run-page-panel">
      {!canRunScan && (
        <div className="run-panel-heading">
          <h1>Pricing</h1>
          <p>{selectedRepositoryLabel || 'Select a GitHub repository before launching a scan.'}</p>
        </div>
      )}

      {canRunScan ? (
        <section className="run-pricing-section" aria-label="Scan pricing">
          {!hasAcceptedScan && (
            <div className="run-pricing-plan-picker">
              <div
                className="run-pricing-tabs"
                role="tablist"
                aria-label="Pentest type"
                onKeyDown={selectPlanFromKeyboard}
              >
                {PRICING_PLANS.map((plan, index) => {
                  const isSelected = selectedPlan === plan.id;
                  const price = pricingEstimateState === 'ready'
                    ? formatPlanPrice(pricingEstimate?.plans?.[plan.id].totalUsd)
                    : '';

                  return (
                    <button
                      key={plan.id}
                      ref={(element) => {
                        tabRefs.current[index] = element;
                      }}
                      id={`run-pricing-tab-${plan.id}`}
                      type="button"
                      role="tab"
                      aria-selected={isSelected}
                      aria-controls="run-pricing-plan-panel"
                      tabIndex={isSelected ? 0 : -1}
                      className={`run-pricing-tab${isSelected ? ' selected' : ''}`}
                      onClick={() => onSelectPlan(plan.id)}
                    >
                      <span>{plan.label}</span>
                      {price && <small>{price}</small>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div
            id="run-pricing-plan-panel"
            className="run-pricing-plan-panel"
            role={hasAcceptedScan ? undefined : 'tabpanel'}
            aria-labelledby={hasAcceptedScan ? undefined : `run-pricing-tab-${selectedPlan}`}
          >
            <PricingStep
              hasAcceptedScan={hasAcceptedScan}
              scanResponse={scanResponse}
              pricingEstimateState={pricingEstimateState}
              pricingEstimateError={pricingEstimateError}
              pricingEstimate={pricingEstimate}
              selectedPlan={selectedPlan}
              launchState={launchState}
              launchError={launchError}
              onViewRun={onViewRun}
            />
          </div>

          {!hasAcceptedScan && (
            <>
              <div className="run-pricing-actions">
                {workspaceName && (
                  <span className="run-pricing-workspace" title="This scan will be added to this workspace">
                    {workspaceName}
                  </span>
                )}
                {selectedPlan === 'free' ? (
                  <button
                    type="button"
                    className="primary-button"
                    disabled={launchState === 'submitting' || isFreeScanUsed}
                    onClick={onOpenEmailPrompt}
                  >
                    {launchState === 'submitting' ? 'Starting...' : 'Start free scan'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="primary-button"
                    disabled={launchState === 'submitting'
                      || pricingEstimateState !== 'ready'
                      || !selectedPlanEstimate}
                    title={isStripeConfigured
                      ? undefined
                      : 'Retry credits remain available; new payments require stripePublishableKey in runtime-config.js'}
                    onClick={onOpenPayment}
                  >
                    {launchState === 'submitting'
                      ? 'Processing payment...'
                      : `Purchase ${selectedPlanInfo.label.toLowerCase()}`}
                  </button>
                )}
              </div>
              {selectedPlan === 'free' && (
                <p className="run-pricing-allowance-note">
                  {isFreeScanUsed
                    ? `You have used today's free scan for this repository. It is available again ${formatNextScanAt(freeScanAllowance?.nextScanAt)}.`
                    : 'One free scan per repository every 24 hours.'}
                </p>
              )}
              {selectedPlan !== 'free' && !isStripeConfigured && (
                <p className="run-pricing-allowance-note">
                  New payments require stripePublishableKey in runtime-config.js. Existing retry credits can still start without a card.
                </p>
              )}
            </>
          )}
        </section>
      ) : (
        <section className="run-settings-list" aria-label="Pricing setup">
          <div className="run-settings-row">
            <div>
              <strong>Repository required</strong>
              <span>Choose a GitHub repository and branch in Scope before starting a scan.</span>
            </div>
            <button type="button" className="primary-button run-panel-action" onClick={onGoToScope}>
              Go to scope
            </button>
          </div>
        </section>
      )}

      <ResultEmailPromptModal
        isOpen={isEmailPromptOpen}
        initialEmail={resultEmail}
        launchState={launchState}
        onCancel={onCloseEmailPrompt}
        onSubmit={onSubmitEmail}
      />
      <PaidScanPaymentModal
        isOpen={isPaymentOpen}
        amountUsd={selectedPlanEstimate?.totalUsd}
        planLabel={selectedPlanInfo.label}
        isSubmitting={launchState === 'submitting'}
        error={launchError}
        onCancel={onClosePayment}
        onError={onPaymentError}
        onSubmit={onSubmitPayment}
      />
    </main>
  );
}
